import axios from "axios";
import { AxiosResponse } from "axios";
import React from "react";
import i18next from "src/i18n/config";

import { useDeleteBuildMutation, useFetchProductBuildsQuery, useImportProductBuildMutation } from "@dashboard/products/api";
import { calculateDownloadProgress, calculateDownloadSpeed } from "@dashboard/products/components/shared";
import { Product } from "@dashboard/products/types";
import { showSuccessToast } from "@shared/toasts/Toasts";
import { showWarningToast } from "@shared/toasts/Toasts";
import { showErrorToast } from "@shared/toasts/Toasts";

import { Props } from "./MainButton";
import { validateZipContent } from "./validateZipContent";

export const getBuildButtonTooltip = (props: Props, mayBuild: boolean) => {
    if (props.isExpired) {
        return {
            title: i18next.t("pages:productPage.tooltip.subscriptionExpired"),
            disabled: true,
        };
    }
    if (props.cannotManageProducts) {
        return {
            title: i18next.t("pages:productPage.tooltip.noPermissionToManageProduct"),
            disabled: true,
        };
    }
    if (props.isBuildLimitReached) {
        return {
            title: i18next.t("pages:productPage.tooltip.buildLimitReached"),
            disabled: true,
        };
    }
    if (!mayBuild) {
        return {
            title: i18next.t("pages:productPage.tooltip.buildingDisabledByConfiguration"),
            disabled: true,
        };
    }

    return {
        title: "",
        disabled: false,
    };
};
export const getImportButtonTooltip = (props: Props, mayImport: boolean) => {
    if (props.isExpired) {
        return {
            title: i18next.t("pages:productPage.tooltip.subscriptionExpired"),
            disabled: true,
        };
    }
    if (props.cannotManageProducts) {
        return {
            title: i18next.t("pages:productPage.tooltip.noPermissionToManageProduct"),
            disabled: true,
        };
    }
    if (props.isBuildLimitReached) {
        return {
            title: i18next.t("pages:productPage.tooltip.buildLimitReached"),
            disabled: true,
        };
    }
    if (!mayImport) {
        return {
            title: i18next.t("pages:productPage.tooltip.importDisabledByConfiguration"),
            disabled: true,
        };
    }

    return {
        title: "",
        disabled: false,
    };
};
export const getSplitButtonTooltip = (props: Props, mayBuild: boolean, mayImport: boolean) => {
    if (props.isExpired) {
        return {
            title: i18next.t("pages:productPage.tooltip.subscriptionExpired"),
            disabled: true,
        };
    }
    if (props.cannotManageProducts) {
        return {
            title: i18next.t("pages:productPage.tooltip.noPermissionToManageProduct"),
            disabled: true,
        };
    }
    if (props.isBuildLimitReached) {
        return {
            title: i18next.t("pages:productPage.tooltip.buildLimitReached"),
            disabled: true,
        };
    }
    if (!mayBuild && !mayImport) {
        return {
            title: i18next.t("pages:productPage.tooltip.buildingAndImportingDisabledByConfiguration"),
            disabled: true,
        };
    }

    return {
        title: "",
        disabled: false,
    };
};

export const useImportFile = (product: Product) => {
    const [buildId, setBuildId] = React.useState<number | null>(null);
    const [name, setName] = React.useState<string | null>(null);
    const [speed, setSpeed] = React.useState("0 MB/s");
    const [progress, setProgress] = React.useState(0);
    const [isUploading, setIsUploading] = React.useState(false);
    const [currentFileIndex, setCurrentFileIndex] = React.useState(0);
    const [totalFiles, setTotalFiles] = React.useState(0);

    const abortControllerRef = React.useRef<AbortController | null>(null);
    const ref = React.useRef<HTMLInputElement>(null);

    const { data: builds = [] } = useFetchProductBuildsQuery(product.id);
    const [importProductBuild] = useImportProductBuildMutation();
    const [deleteBuild] = useDeleteBuildMutation();

    const onClear = () => {
        setIsUploading(false);
        setName(null);
        setSpeed("0 MB/s");
        setProgress(0);
        setBuildId(null);
        setCurrentFileIndex(0);
        setTotalFiles(0);
        abortControllerRef.current = null;
        if (ref.current) {
            ref.current.value = "";
        }
    };
    const onChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        setIsUploading(true);
        abortControllerRef.current = new AbortController();
        const file = e.target.files?.[0];
        if (file) {
            try {
                const zipContent = await validateZipContent(file);

                const currentProductVersion = product.androidVersion.split(".")[0];
                const zipProductVersion = zipContent.product.androidVersion.split(".")[0];
                if (currentProductVersion !== zipProductVersion) {
                    throw new Error(
                        i18next.t("pages:productPage.toast.androidVersionMismatch", {
                            productVersion: currentProductVersion,
                            zipVersion: zipProductVersion,
                        }),
                    );
                }

                if (product.codename !== zipContent.product.codename) {
                    throw new Error(
                        i18next.t("pages:productPage.toast.codenameMismatch", {
                            productCodename: product.codename,
                            zipCodename: zipContent.product.codename,
                        }),
                    );
                }

                if (builds.some((build) => build.version === zipContent.build.version)) {
                    throw new Error(
                        i18next.t("pages:productPage.toast.buildVersionExists", { version: zipContent.build.version }),
                    );
                }

                const importResult = await importProductBuild({
                    productId: product.id,
                    ...zipContent,
                });
                if ("error" in importResult) {
                    return;
                }
                const result = importResult.data;

                setBuildId(result.build.buildId);

                if (result.uploadURLs.length) {
                    setTotalFiles(result.uploadURLs.length);
                    let fileIndex = 0;
                    for (const uploadURL of result.uploadURLs) {
                        const fileMeta = result.build.artifacts?.find((bf) => bf.id === uploadURL.id);

                        if (!fileMeta) {
                            continue;
                        }
                        const matchingFile = zipContent.files.find((item) => {
                            return item.name === fileMeta.name;
                        });
                        if (!matchingFile) {
                            continue;
                        }

                        fileIndex += 1;
                        setCurrentFileIndex(fileIndex);
                        setName(fileMeta.name);
                        const uploadStartTime = Date.now();
                        const presignedApi = axios.create({ url: uploadURL.uploadURL });
                        // eslint-disable-next-line no-await-in-loop
                        await presignedApi.put<Blob, AxiosResponse<Blob>>(uploadURL.uploadURL, matchingFile.content, {
                            headers: { "Content-Type": matchingFile.content.type },
                            signal: abortControllerRef.current?.signal,
                            onUploadProgress: (progressEvent) => {
                                if (progressEvent.total) {
                                    const percent = calculateDownloadProgress(progressEvent.loaded, progressEvent.total);
                                    setProgress(percent);

                                    const downloadSpeed = calculateDownloadSpeed(progressEvent.loaded, uploadStartTime);
                                    setSpeed(downloadSpeed);
                                }
                            },
                        });
                    }
                }

                showSuccessToast(i18next.t("pages:productPage.toast.importedBuildSuccessfully"));
            } catch (err) {
                const error = err as Error;
                if (error.name === "CanceledError") {
                    showWarningToast(i18next.t("pages:productPage.toast.uploadCancelled"));
                } else {
                    showErrorToast(error.message);
                }
            } finally {
                onClear();
            }
        }
    };
    const onAbort = async () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        if (product.id && buildId) {
            await deleteBuild({ productId: product.id, buildId });
        }
        onClear();
    };

    return { ref, isUploading, name, speed, progress, currentFileIndex, totalFiles, onAbort, onChange };
};
